'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { ArrowRight, X, ArrowUpRight } from "lucide-react";
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import LocaleChange from '@/app/lib/localeChange';

export default function Header() {
  const t = useTranslations('Footer');
  const locale = useLocale();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto';
  }, [isOpen]);

  const onSelectLanguage = async (newLocale: string) => {
    if (locale !== newLocale) {
      document.cookie = `NEXT_LOCALE=${newLocale}; path=/; max-age=31536000; SameSite=Lax`;
      LocaleChange(newLocale);
    }
  };
  return (
    <header className="fixed w-full top-0 left-0 z-50 px-10 py-5 h-[100px]">
      <div className="flex items-center justify-between p-6">
        <span>{" "}</span>
        {/* Menu Button */}
        <button
          onClick={() => setIsOpen(true)}
          className="focus:outline-none rounded-full bg-gradient-primary px-6 py-2 text-sm cursor-pointer hover:opacity-90"
        >
          Menu
        </button>
      </div>
      {/* Fullscreen Menu Overlay */}
      <div
        className={`fixed inset-0 bg-menu-list 
                    flex flex-col md:flex-row items-center justify-center transition-transform duration-500
                    ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Close Button */}
        <button
          onClick={() => setIsOpen(false)}
          aria-label="close"
          className="absolute top-6 right-6 text-white text-3xl cursor-pointer hover:text-violet-400"
        >
          <X size={32} />
        </button>

        {/* Left side - Menu */}
        <nav className="flex-1 flex flex-col justify-center items-start px-4 md:px-40 space-y-8">
          <div className='flex justify-end my-4 ml-auto w-full'>
              <select 
                aria-label="Select website language"
                className='bg-transparent text-gray-300 text-xl border border-gray-500 rounded-md p-1 cursor-pointer outline-none hover:text-orange-300 hover:border-orange-300 transition-colors'
                onChange={(e) => onSelectLanguage(e.target.value)}
                defaultValue={locale}
              >
                <option value="en" className="text-sm md:text-md text-black bg-gray-400">EN - English</option>
                <option value="sq" className="text-sm md:text-md text-black bg-gray-400">SQ - Shqip</option>
                <option value="fr" className="text-sm md:text-md text-black bg-gray-400">FR - Français</option>
                <option value="de" className="text-sm md:text-md text-black bg-gray-400">DE - Deutsch</option>
                <option value="es" className="text-sm md:text-md text-black bg-gray-400">ES - Español</option>
                <option value="ar" className="text-sm md:text-md text-black bg-gray-400">AR - العربية</option>
              </select>
            </div>
          <hr className='w-full text-gray-500 border opacity-20 mb-8' />
          {[
            { name: "home", href: "/" },
            { name: "about", href: "/about-us" },
            { name: "services", href: "/services" },
            { name: "projects", href: "/projects" },
            { name: "blogs", href: "/blog" },
            { name: "contact_us", href: "/contact-us" },
          ].map((item, index) => (
            <Link
              key={index}
              href={item?.href}
              onClick={() => setIsOpen(false)}
              className="relative group transition-colors flex justify-between w-full"
            >
              {/* Text */}
              <div className='flex gap-4 items-end'>
                <span className="group-hover:text-violet-400 transition-colors text-3xl md:text-5xl text-white">
                  {t(item?.name)}
                </span>
              </div>
              <ArrowRight className="text-white w-10 h-10 border-1 rounded-full px-2 hidden md:block opacity-100 transition-all duration-300 group-hover:text-violet-400" />
            </Link>
          ))}
          <hr className='w-full text-gray-500 border opacity-20 mt-0' />
          <div className='w-full flex gap-8'>
            <Image
              src={"/images/logo_white.png"}
              width={130}
              height={20}
              alt="primalt_logo"
            />
          </div>
          <div className='w-full flex gap-8 mb-8'>
            <Link href="https://www.instagram.com/primalt.ai/" target="_blank" className="hover:text-violet-400 cursor-pointer pb-2 transition-colors text-lg md:text-xl text-white flex gap-2">
              INSTAGRAM <ArrowUpRight />
            </Link>
            <Link href="https://www.linkedin.com/company/primalt/" target="_blank" className="hover:text-violet-400 cursor-pointer pb-2 transition-colors text-lg md:text-xl text-white flex gap-2">
              LINKEDIN <ArrowUpRight />
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
}
